import React, { useMemo, useState, useEffect } from 'react'

import {
  UNIT_PX,
  convertUnits,
  nextToggleUnits,
  defaultUnitsForPlatform,
} from 'utils/unitHelpers'
import { selectableCategoryOptionGroups } from 'utils/categoryTreeOptions'
import { clampPositive } from 'utils/math'
import type { SelectOption } from 'utils/formHelpers'
import { groupedOptionsFromHash } from 'utils/formHelpers'

import type { CardButtonOption } from 'components/CardButtonSelect'
import CardButtonSelect from 'components/CardButtonSelect'
import type {
  PageSizeType,
  UnitType,
  DocumentRenderType,
  GridStyle,
} from 'EditorApp/types'

export interface CategoryNode {
  readonly name: string
  readonly gridStyle: GridStyle
  readonly children: Record<number, CategoryNode>
}

export interface PageSize {
  readonly value: number
  readonly text: string
  readonly width: number
  readonly height: number
  readonly categoryId: number
}

interface Props {
  readonly pageSizes: Array<PageSize>
  readonly categoryTree: Record<number, CategoryNode>
  readonly renderType: DocumentRenderType
  // Hides the platform row, for when the platform belongs to an existing document.
  readonly fixedPlatform?: boolean
  readonly platformId: number | null
  readonly categoryId: number | null
  readonly pageSize: PageSizeType
  readonly width: number
  readonly height: number
  readonly onChangeRenderType?: (renderType: DocumentRenderType) => void
  readonly onChangePlatformId?: (platformId: number) => void
  readonly onChangeCategoryId: (categoryId: number | null) => void
  readonly onChangePageSize: (pageSize: PageSizeType) => void
  readonly onChangeWidth: (width: number) => void
  readonly onChangeHeight: (height: number) => void
  readonly onChangeSizeSelected: (selected: boolean) => void
}

interface CustomSizeFieldProps {
  readonly name: string
  readonly size: number
  readonly units: string
  readonly onToggleUnits: () => void
  readonly onChange: (value: number) => void
}

const optionsFromNode = (node: Record<number, CategoryNode>) => {
  return Object.entries(node).map(([id, item]) => {
    return {
      key: id,
      text: item.name,
      value: Number(id),
    }
  })
}

const CustomSizeField = ({
  name,
  size,
  units,
  onToggleUnits,
  onChange,
}: CustomSizeFieldProps) => {
  return (
    <div className="field">
      <div>
        <label htmlFor={`template_page_size_${name}`}>custom {name}</label>{' '}
        <button
          type="button"
          aria-label={`${units} (toggle units between pixels, milimetres, inches)`}
          className="UnitsToggle"
          onClick={onToggleUnits}
        >
          {units}
        </button>
      </div>
      <input
        id={`template_page_size_${name}`}
        value={size > 0 ? convertUnits(size, UNIT_PX, units) : ''}
        type="number"
        required
        min="1"
        onChange={(ev) =>
          onChange(convertUnits(clampPositive(ev.target.value), units, UNIT_PX))
        }
      />
      <input
        type="hidden"
        name={`template[page_size_attributes][${name}]`}
        value={size}
      />
    </div>
  )
}

const TemplatePageSizeSelector = ({
  pageSizes,
  categoryTree,
  renderType,
  fixedPlatform = false,
  platformId,
  categoryId,
  pageSize,
  width,
  height,
  onChangeRenderType,
  onChangePlatformId,
  onChangeCategoryId,
  onChangePageSize,
  onChangeWidth,
  onChangeHeight,
  onChangeSizeSelected,
}: Props) => {
  // Picking a platform normally sets the units, so with the row hidden they come from the platform
  // given.
  const [units, setUnits] = useState<string>(() =>
    fixedPlatform && platformId
      ? defaultUnitsForPlatform(categoryTree[platformId]?.name)
      : UNIT_PX
  )
  const customSized = pageSize === 'custom'

  useEffect(() => {
    if (customSized && width > 0 && height > 0) {
      onChangeSizeSelected(true)
    } else if (Number.isInteger(pageSize)) {
      onChangeSizeSelected(true)
    } else {
      onChangeSizeSelected(false)
    }
  }, [onChangeSizeSelected, pageSize, customSized, width, height])

  const updateCustomSize = (width: number, height: number) => {
    onChangeWidth(width)
    onChangeHeight(height)
  }

  const toggleUnits = () => {
    setUnits(nextToggleUnits(units))
  }

  const updatePageSize = (
    pageSize: PageSizeType,
    size?: { width: number; height: number }
  ) => {
    onChangePageSize(pageSize)

    if (size) {
      updateCustomSize(size.width, size.height)
    } else {
      updateCustomSize(0, 0)
    }
  }

  const setSubcategory = (categoryId: number | null) => {
    updatePageSize(null)
    onChangeCategoryId(categoryId)
  }

  const setPlatform = ({
    platformId,
    units,
    renderType,
  }: {
    platformId: number
    units: UnitType
    renderType: DocumentRenderType
  }) => {
    setSubcategory(null)
    onChangePlatformId?.(platformId)
    if (onChangeRenderType) onChangeRenderType(renderType)
    setUnits(units)
  }

  const CategoryOptions = useMemo(() => {
    if (!platformId) return []

    const platform = categoryTree[platformId]
    if (!platform || !Object.keys(platform.children).length) return null

    const groups = selectableCategoryOptionGroups(platform) as Record<
      string,
      SelectOption[]
    >

    return groupedOptionsFromHash(groups, 'value', 'text')
  }, [platformId, categoryTree])

  const PageSizeOptions = useMemo(() => {
    if (!pageSizes || !categoryId) return null

    const pageSizeOptions = pageSizes.reduce(
      (acc: Array<CardButtonOption>, ps) => {
        if (ps.categoryId !== categoryId) return acc

        const width = convertUnits(ps.width, UNIT_PX, units)
        const height = convertUnits(ps.height, UNIT_PX, units)

        acc.push({
          key: ps.value,
          text: ps.text,
          subText: `${width}${units} x ${height}${units}`,
          value: ps.value,
          size: width > height ? 'wide' : height > width ? 'tall' : 'square',
          callbackData: { width, height },
        })

        return acc
      },
      []
    )

    return [
      ...pageSizeOptions,
      {
        key: 'custom',
        text: 'Custom',
        value: 'custom',
        size: 'square',
      },
    ]
  }, [pageSizes, categoryId, units])

  const PlatformOptions = useMemo(() => {
    const options = optionsFromNode(categoryTree).map((item) => {
      return {
        ...item,
        callbackData: {
          units: defaultUnitsForPlatform(item.text),
          platformId: item.value,
          renderType: item.value <= 0 ? 'video' : 'static',
        },
      }
    })

    if (onChangeRenderType) {
      return options
    }

    return options.filter(
      (option) => option.callbackData.renderType === renderType
    )
  }, [categoryTree, onChangeRenderType, renderType])

  return (
    <>
      {!fixedPlatform && (
        <div className="field">
          <label htmlFor="platform_name_select">platform</label>
          <CardButtonSelect
            options={PlatformOptions}
            id="platform_name_select"
            inputId="platform_name"
            selectedId={platformId}
            onClick={(_, callbackData) => setPlatform(callbackData)}
          />
        </div>
      )}

      {platformId && (
        <div className="field">
          <label htmlFor="category_name">category</label>
          <select
            id="category_name"
            name="template[category_id]"
            className="CreateTemplate__select"
            onChange={(ev) => setSubcategory(clampPositive(ev.target.value))}
            value={categoryId ?? ''}
          >
            <option value="" disabled hidden />
            {CategoryOptions}
          </select>
        </div>
      )}

      {PageSizeOptions && (
        <div className="field">
          <label htmlFor="page_size_select">page size</label>
          <CardButtonSelect
            options={PageSizeOptions}
            id="page_size_select"
            inputId="page_size"
            inputName="template[page_size]"
            onClick={(value, callbackData) =>
              updatePageSize(value as PageSizeType, callbackData)
            }
            selectedId={pageSize}
          />
        </div>
      )}

      {customSized && (
        <>
          <CustomSizeField
            name="width"
            size={width}
            units={units}
            onToggleUnits={toggleUnits}
            onChange={(width) => updateCustomSize(width, height)}
          />
          <CustomSizeField
            name="height"
            size={height}
            units={units}
            onToggleUnits={toggleUnits}
            onChange={(height) => updateCustomSize(width, height)}
          />
        </>
      )}
    </>
  )
}

export default TemplatePageSizeSelector
